/**
 * Reads a built `TableModel` into the shapes the chart and KPI tiles draw:
 * category/series grids for bar and line charts, slices for a pie, and one
 * formatted cell for a KPI. The statistic axis carries one leaf per
 * statistic, so every adapter first narrows that axis to the wanted
 * statistic (falling back to the first one the table holds). A chart
 * draws one unit: the rows (down leaves) in another unit than its first
 * drawn row are left out and named in `omitted`.
 */

import type {
  AxisLeaf,
  ModelCell,
  NumberFormatSpec,
  TableModel,
} from "utils/tableModel"
import { formatCell } from "utils/tableModel"

export interface CategorySeries {
  categories: { id: string; label: string }[]
  series: { id: string; label: string; values: (number | null)[] }[]
  format: NumberFormatSpec | null
  /** The labels of the rows left out because they are in another unit. */
  omitted: string[]
}

export const leafLabel = (leaf: AxisLeaf): string =>
  leaf.path[leaf.path.length - 1] ?? leaf.id

/** The unit a table row's values are in; a chart draws one. */
export type ChartUnit = "percent" | "points" | "mean" | "count" | "rank"

/**
 * What a measure row shows in the Statistic column for each unit (see
 * `applyMeasures`), which is also how a chart knows a measure row's unit.
 */
export const UNIT_LABEL: Record<ChartUnit, string> = {
  percent: "%",
  points: "pts",
  mean: "mean",
  count: "",
  rank: "rank",
}

const UNIT_OF_LABEL = new Map(
  (Object.entries(UNIT_LABEL) as [ChartUnit, string][]).map(([u, l]) => [l, u])
)

/**
 * The unit of row `r` read over `columns`: a measure row's by its unit
 * label, a category row's by its first formatted cell (a percentage, a
 * points or index figure, a mean, else a count).
 */
const rowUnit = (model: TableModel, r: number, columns: number[]): ChartUnit => {
  const label = model.down.leaves[r]?.statLabel
  const labelled = label === undefined ? undefined : UNIT_OF_LABEL.get(label)
  if (labelled) return labelled
  const cells = columns.map((c) => model.cells[r]?.[c])
  const cell = cells.find((x) => x?.format) ?? cells.find((x) => x)
  if (!cell) return "count"
  if (cell.format?.percent || cell.kind === "percent") return "percent"
  if (cell.kind === "index") return "points"
  if (cell.kind === "mean" || cell.kind === "minmax") return "mean"
  return "count"
}

/** The hint under a chart that left rows out: "Not drawn (other unit): NPS". */
export const omittedNote = (omitted: string[]): string | null =>
  omitted.length > 0 ? `Not drawn (other unit): ${omitted.join(", ")}` : null

const numeric = (cell: ModelCell | undefined): number | null =>
  cell && typeof cell.raw === "number" && Number.isFinite(cell.raw)
    ? cell.raw
    : null

/**
 * Indices of the leaves on `axis` that carry `statistic`, or of the first
 * statistic when the table lacks it, or every leaf when the axis has no stats.
 */
const statIndices = (
  model: TableModel,
  axis: "down" | "across",
  statistic: string
): number[] => {
  const leaves = model[axis].leaves
  const hasStats = leaves.some((l) => l.stat !== undefined)
  if (!hasStats) return leaves.map((_, i) => i)
  const wanted = leaves
    .map((l, i) => (l.stat === statistic ? i : -1))
    .filter((i) => i >= 0)
  if (wanted.length > 0) return wanted
  const first = leaves[0]?.stat
  return leaves.map((l, i) => (l.stat === first ? i : -1)).filter((i) => i >= 0)
}

const cellAt = (
  model: TableModel,
  r: number,
  c: number
): ModelCell | undefined => model.cells[r]?.[c]

/**
 * `indices` of `axis` without its Totals leaves, unless `keepTotals`. An axis
 * that holds nothing but Totals (a table with no column variable) keeps
 * them, so it still has something to draw.
 */
const withoutTotals = (
  model: TableModel,
  axis: "down" | "across",
  indices: number[],
  keepTotals: boolean
): number[] => {
  if (keepTotals) return indices
  const kept = indices.filter((i) => !model[axis].leaves[i].isTotal)
  return kept.length > 0 ? kept : indices
}

/**
 * Categories on one axis, series on the other, values for `statistic`.
 * `hidden` leaves out categories, `hiddenSeries` series, by id. The rows
 * (the categories when they run down, else the series) are then kept in
 * the unit of the first of them; the others are left out and named in
 * `omitted`, and the format is the first kept row's.
 */
const cross = (
  model: TableModel,
  categoryAxis: "down" | "across",
  statistic: string,
  keepTotals: { categories: boolean; series: boolean },
  hidden: Set<string>,
  hiddenSeries: Set<string> = new Set()
): CategorySeries => {
  const seriesAxis = categoryAxis === "down" ? "across" : "down"
  const shownCats = withoutTotals(
    model,
    categoryAxis,
    statIndices(model, categoryAxis, statistic),
    keepTotals.categories
  ).filter((i) => !hidden.has(model[categoryAxis].leaves[i].id))
  const shownSeries = withoutTotals(
    model,
    seriesAxis,
    statIndices(model, seriesAxis, statistic),
    keepTotals.series
  ).filter((i) => !hiddenSeries.has(model[seriesAxis].leaves[i].id))
  const rowsDown = categoryAxis === "down"
  const rows = rowsDown ? shownCats : shownSeries
  const columns = rowsDown ? shownSeries : shownCats
  const units = new Map(rows.map((r) => [r, rowUnit(model, r, columns)]))
  const drawn = rows.length > 0 ? units.get(rows[0]) : undefined
  const kept = rows.filter((r) => units.get(r) === drawn)
  const omitted = [
    ...new Set(
      rows
        .filter((r) => units.get(r) !== drawn)
        .map((r) => leafLabel(model.down.leaves[r]))
    ),
  ]
  const catIdx = rowsDown ? kept : shownCats
  const serIdx = rowsDown ? shownSeries : kept
  const cellOf = (ci: number, si: number): ModelCell | undefined =>
    rowsDown ? cellAt(model, ci, si) : cellAt(model, si, ci)
  const firstRow = kept[0]
  const format =
    firstRow === undefined
      ? null
      : (columns
          .map((c) => cellAt(model, firstRow, c)?.format)
          .find((f): f is NumberFormatSpec => Boolean(f)) ?? null)
  return {
    categories: catIdx.map((i) => ({
      id: model[categoryAxis].leaves[i].id,
      label: leafLabel(model[categoryAxis].leaves[i]),
    })),
    series: serIdx.map((si) => ({
      id: model[seriesAxis].leaves[si].id,
      label: leafLabel(model[seriesAxis].leaves[si]),
      values: catIdx.map((ci) => numeric(cellOf(ci, si))),
    })),
    format,
    omitted,
  }
}

/**
 * The down and across leaves of the cell a bar, line or pie chart draws for
 * category `categoryId` of series `seriesId`, its categories running along
 * `categoryAxis`: the leaves with those ids that carry the statistic the
 * chart draws (see `statIndices`). Null when either is not in the model.
 */
export const chartCellLeaves = (
  model: TableModel,
  opts: {
    categoryAxis: "down" | "across"
    categoryId: string
    seriesId: string
    statistic: string
  }
): { down: AxisLeaf; across: AxisLeaf } | null => {
  const seriesAxis = opts.categoryAxis === "down" ? "across" : "down"
  const find = (axis: "down" | "across", id: string): AxisLeaf | undefined =>
    statIndices(model, axis, opts.statistic)
      .map((i) => model[axis].leaves[i])
      .find((l) => l.id === id)
  const category = find(opts.categoryAxis, opts.categoryId)
  const series = find(seriesAxis, opts.seriesId)
  if (!category || !series) return null
  return opts.categoryAxis === "down"
    ? { down: category, across: series }
    : { down: series, across: category }
}

/**
 * Categories on the axis opposite `seriesAxis` (a physical axis of the
 * model). Totals categories are dropped, and so is the Totals series unless
 * `showTotals`; an axis with nothing but Totals (no column variable) keeps
 * them, so a tile with rows and no columns draws one series.
 * `hiddenCategories` leaves categories out by id (a tile hides its rows in
 * its view instead, through its category display).
 */
export const modelToBarSeries = (
  model: TableModel,
  opts: {
    statistic: string
    seriesAxis: "across" | "down"
    showTotals: boolean
    hiddenCategories?: string[]
  }
): CategorySeries =>
  cross(
    model,
    opts.seriesAxis === "across" ? "down" : "across",
    opts.statistic,
    { categories: false, series: opts.showTotals },
    new Set(opts.hiddenCategories)
  )

/**
 * `series` with its categories ordered by the first series' values:
 * largest first ("desc") or smallest first ("asc"), missing values last and
 * ties in table order. Every series follows the same order. "none" returns
 * `series` itself.
 */
export const sortCategories = (
  series: CategorySeries,
  sort: "none" | "desc" | "asc"
): CategorySeries => {
  const first = series.series[0]
  if (sort === "none" || !first) return series
  const sign = sort === "desc" ? -1 : 1
  const order = series.categories
    .map((_, i) => i)
    .sort((a, b) => {
      const va = first.values[a] ?? null
      const vb = first.values[b] ?? null
      if (va === null || vb === null) {
        return va === vb ? a - b : va === null ? 1 : -1
      }
      return sign * (va - vb) || a - b
    })
  return {
    ...series,
    categories: order.map((i) => series.categories[i]),
    series: series.series.map((s) => ({
      ...s,
      values: order.map((i) => s.values[i] ?? null),
    })),
  }
}

/**
 * `series` with each category's values rescaled to shares of the
 * category's total across the series, so every stack sums to 1 and reads
 * as 100% (the tables' own scale for percentages). The format becomes a
 * percentage: the series' own decimals when it already was one, else one
 * decimal. A category whose total is not positive keeps no values.
 * Meant for shares of counts or percentages, which are never negative: a
 * negative value is left out of its category's total and comes out as a
 * negative share, so a stack that mixes signs does not sum to 1.
 */
export const normalizeStacks = (series: CategorySeries): CategorySeries => {
  const totals = series.categories.map((_, i) =>
    series.series.reduce((sum, s) => sum + Math.max(0, s.values[i] ?? 0), 0)
  )
  return {
    ...series,
    series: series.series.map((s) => ({
      ...s,
      values: s.values.map((v, i) =>
        v === null || totals[i] <= 0 ? null : v / totals[i]
      ),
    })),
    format: {
      decimals: series.format?.percent ? series.format.decimals : 1,
      thousands: false,
      percent: true,
    },
  }
}

/**
 * A bar tile's series as it draws them: stacks scaled to 100% when
 * `normalize` is on in stacked mode, then the categories sorted. The screen
 * and the PowerPoint export both draw this.
 */
export const shapeBarSeries = (
  series: CategorySeries,
  opts: {
    mode: "grouped" | "stacked"
    normalize: boolean
    sort: "none" | "desc" | "asc"
  }
): CategorySeries =>
  sortCategories(
    opts.mode === "stacked" && opts.normalize
      ? normalizeStacks(series)
      : series,
    opts.sort
  )

const lastSegment = (path: string): string => path.split("//").pop() ?? path

/**
 * On a banner (several column variables, each leaf under its variable's
 * label) the across leaves that are not the wave variable's: the named one
 * when the table has it, else the first. Empty for a single variable.
 */
const offWaveLeaves = (
  model: TableModel,
  waveVariable: string | undefined
): Set<string> => {
  const banner = model.across.leaves.filter(
    (l) => !l.isTotal && l.path.length > 1
  )
  const groups = [...new Set(banner.map((l) => l.path[0]))]
  if (groups.length < 2) return new Set()
  const named = waveVariable ? lastSegment(waveVariable) : ""
  const wave = groups.includes(named) ? named : groups[0]
  return new Set(banner.filter((l) => l.path[0] !== wave).map((l) => l.id))
}

/**
 * x categories = across leaves, one series per down leaf, no Totals (unless
 * Totals are all an axis has). With several column variables only the
 * `waveVariable`'s categories make the x axis (the first variable's when it
 * is unset or not in the table).
 */
export const modelToLineSeries = (
  model: TableModel,
  opts: { statistic: string; hiddenSeries: string[]; waveVariable?: string }
): CategorySeries =>
  cross(
    model,
    "across",
    opts.statistic,
    { categories: false, series: false },
    offWaveLeaves(model, opts.waveVariable),
    new Set(opts.hiddenSeries)
  )

/**
 * Down categories against the Totals column (or the first across leaf);
 * empty and negative slices are dropped, and so are the rows in another
 * unit than the first (named in `omitted`).
 */
export const modelToPieSlices = (
  model: TableModel,
  opts: { statistic: string; hiddenCategories?: string[] }
): {
  slices: { id: string; label: string; value: number }[]
  format: NumberFormatSpec | null
  omitted: string[]
  /** The id of the column the slices are read from. */
  column: string | null
} => {
  const all = cross(
    model,
    "down",
    opts.statistic,
    { categories: false, series: true },
    new Set(opts.hiddenCategories)
  )
  const totals =
    all.series.find((s) =>
      model.across.leaves.some((l) => l.id === s.id && l.isTotal)
    ) ?? all.series[0]
  if (!totals) {
    return { slices: [], format: all.format, omitted: all.omitted, column: null }
  }
  return {
    slices: all.categories
      .map((c, i) => ({
        id: c.id,
        label: c.label,
        value: totals.values[i] ?? 0,
      }))
      .filter((s) => s.value > 0),
    format: all.format,
    omitted: all.omitted,
    column: totals.id,
  }
}

/** Pie slices ordered by value as `sortCategories` orders categories. */
export const sortSlices = <S extends { id: string; label: string; value: number }>(
  slices: S[],
  sort: "none" | "desc" | "asc"
): S[] => {
  if (sort === "none") return slices
  const sorted = sortCategories(
    {
      categories: slices,
      series: [{ id: "value", label: "", values: slices.map((s) => s.value) }],
      format: null,
      omitted: [],
    },
    sort
  )
  return sorted.categories as S[]
}

/**
 * Index of the leaf `id` carrying `statistic` (`null` = any). Without an id,
 * or when the id is not found, the first Totals leaf when `preferTotal`,
 * else the first non-Totals leaf.
 */
const findLeaf = (
  leaves: AxisLeaf[],
  id: string | null,
  statistic: string | null,
  preferTotal: boolean
): number => {
  const matches = (l: AxisLeaf) =>
    statistic === null || l.stat === undefined || l.stat === statistic
  if (id !== null) {
    const i = leaves.findIndex((l) => l.id === id && matches(l))
    if (i >= 0) return i
  }
  if (preferTotal) {
    const t = leaves.findIndex((l) => l.isTotal && matches(l))
    if (t >= 0) return t
  }
  return leaves.findIndex((l) => !l.isTotal && matches(l))
}

/**
 * The base behind the cell at `r`, `c`: the "n" of the Totals category in the
 * same banner position (the column total), or for `row%` the category's own
 * "n" in the Totals banner position (the row total). Undefined when the table
 * has no "n" statistic or no Totals.
 */
const baseCell = (
  model: TableModel,
  r: number,
  c: number,
  statistic: string
): ModelCell | undefined => {
  const down = model.down.leaves
  const across = model.across.leaves
  const rowBase = statistic === "row%"
  if (model.statAxis === "down") {
    if (rowBase) {
      const nRow = down.findIndex((l) => l.id === down[r].id && l.stat === "n")
      const totalCol = across.findIndex((l) => l.isTotal)
      return nRow >= 0 && totalCol >= 0
        ? cellAt(model, nRow, totalCol)
        : undefined
    }
    const totalRow = down.findIndex((l) => l.isTotal && l.stat === "n")
    return totalRow >= 0 ? cellAt(model, totalRow, c) : undefined
  }
  if (rowBase) {
    const nCol = across.findIndex(
      (l) => l.id === across[c].id && l.stat === "n"
    )
    const totalRow = down.findIndex((l) => l.isTotal)
    return nCol >= 0 && totalRow >= 0
      ? cellAt(model, totalRow, nCol)
      : undefined
  }
  const totalCol = across.findIndex((l) => l.isTotal && l.stat === "n")
  return totalCol >= 0 ? cellAt(model, r, totalCol) : undefined
}

/**
 * Index of the column `modelCell` reads for `opts`: the named column, else
 * Totals, else the first column; -1 when there is none.
 */
export const modelCellColumn = (
  model: TableModel,
  opts: { column: string | null; statistic: string }
): number =>
  findLeaf(
    model.across.leaves,
    opts.column,
    model.statAxis === "across" ? opts.statistic : null,
    true
  )

/**
 * One cell for a KPI: the named row (first category by default) in the named
 * column (Totals by default), with the formatted base (see `baseCell`) and
 * the cell's leaves.
 */
export const modelCell = (
  model: TableModel,
  opts: { row: string | null; column: string | null; statistic: string }
): {
  text: string
  raw: number | string | null
  rowLabel: string
  columnLabel: string
  baseText: string | null
  down: AxisLeaf
  across: AxisLeaf
} | null => {
  const r = findLeaf(
    model.down.leaves,
    opts.row,
    model.statAxis === "down" ? opts.statistic : null,
    false
  )
  const c = findLeaf(
    model.across.leaves,
    opts.column,
    model.statAxis === "across" ? opts.statistic : null,
    true
  )
  if (r < 0 || c < 0) return null
  const cell = cellAt(model, r, c)
  if (!cell) return null
  const base = baseCell(model, r, c, opts.statistic)
  const baseText = base ? formatCell(base) : null
  return {
    text: formatCell(cell),
    raw: cell.raw,
    rowLabel: leafLabel(model.down.leaves[r]),
    columnLabel: leafLabel(model.across.leaves[c]),
    baseText,
    down: model.down.leaves[r],
    across: model.across.leaves[c],
  }
}
